/**
 * 调 account 服务。**纯函数 + fetch，不含 React** —— 能用 node:test 直接测。
 *
 * token 只放在内存和 localStorage 里，**不往 URL、不往日志**。
 */

import type { MessageKey } from '../i18n/i18n.ts';
import { decodePhoneConfig } from './phone.ts';

export interface Session {
  uid: string;
  token: string;
  expiresAt: string;
}

export class AuthError extends Error {
  code: string;
  status: number;
  detail?: Record<string, string>;

  constructor(code: string, msg: string, status: number, detail?: Record<string, string>) {
    super(msg);
    this.name = 'AuthError';
    this.code = code;
    this.status = status;
    this.detail = detail;
  }
}

/**
 * 后端错误 → 文案键。**后端只回稳定的 code，文案在前端翻** ——
 * 后端不知道这个用户选了哪种语言，而且同一个 code 在不同界面上
 * 可能要用不同的说法。
 *
 * 认不出来的 code 一律落到 `err.UNKNOWN`：**新加的后端错误不该让界面空白**。
 */
const KNOWN = new Set([
  'BAD_CREDENTIALS', 'IDENTIFIER_TAKEN', 'WEAK_PASSWORD', 'ACCOUNT_LOCKED',
  'ACCOUNT_DISABLED', 'NO_SESSION', 'BAD_PHONE', 'NETWORK',
  'REGISTRATION_COUNTRY_DISABLED', 'BAD_REQUEST', 'ALREADY_CLAIMED', 'INSUFFICIENT_FUNDS',
  'BAD_CPF', 'IDENTITY_TAKEN', 'IDENTITY_ALREADY_BOUND',
  'NO_PAYOUT_IDENTITY', 'WAGER_NOT_MET', 'WITHDRAW_TOO_SMALL',
  'WITHDRAW_OVER_LIMIT', 'WITHDRAW_NOT_PENDING', 'NOTHING_TO_CLAIM',
  'BAD_NICKNAME', 'PRODUCT_UNAVAILABLE', 'PURCHASE_LIMIT', 'CREDIT_FAILED',
  'GAME_UNAVAILABLE', 'NICKNAME_RENAME_LIMIT', 'NO_SUCH_MAIL',
]);

/**
 * 收款身份（提现打给谁）。
 *
 * `masked` 是 `***.456.789-**` —— **后端只下发打码的号**，
 * 所以这个类型里根本没有存放完整号码的位置。
 */
export interface PayoutIdentity {
  kind: string;
  bound: boolean;
  identity: {
    masked: string;
    holderName: string;
    /** 三方实名核验过没有。现在还没接，所以永远是 false。 */
    verified: boolean;
    boundAt: string | null;
  } | null;
}

/** 一件商品。**每个玩家看到的不一样** —— 限购剩余次数是按人算的。 */
export interface Product {
  code: string;
  kind: string;
  /** 实付 */
  price: number;
  /** 到账（含赠送）。特卖时会大于 price */
  coins: number;
  /** 划线价。**只在确实更贵时才有** —— 后端不会给一个和现价一样的数字 */
  originalPrice: number | null;
  onSale: boolean;
  saleTo: string | null;
  /** 角标：hot / best / daily / first…，前端翻成文案 */
  tag: string | null;
  dailyLimit: number | null;
  dailyUsed: number;
  left: number | null;
  /** **能不能买由服务端说** —— 前端自己拼 `used < limit` 的话，
   *  以后加了别的限制（等级门槛、活动期），那条规则就悄悄过期了 */
  canBuy: boolean;
}

export interface PurchaseOrder {
  orderNo: string;
  code: string;
  price: number;
  coins: number;
  /** created 待支付 / paid 已支付待到账 / credited 已到账 / cancelled / expired */
  status: string;
  channel: string;
  ts: number | null;
  creditedTs: number | null;
  /**
   * PIX 的「复制粘贴」串。**手机上这个才是主路径** ——
   * 复制 → 切到银行 App → 粘贴。二维码是给另一台设备扫的。
   *
   * **只在 `status === 'created'` 时有值**：已经付掉的单再把码发出来，
   * 只会让人再付一次。
   */
  payCode?: string | null;
  /** 渠道自己的收款页（二维码在那上面）。PIX 场景下常常是空的。 */
  payUrl?: string | null;
  /** 付款码什么时候失效（秒）。界面上要倒计时。 */
  expiresAt?: number | null;
  failReason?: string | null;
}

/** 还能不能付。**这三个状态之外都不用再轮询了。** */
export function isPending(status: string): boolean {
  return status === 'created' || status === 'paid';
}

export function errorKey(e: unknown): MessageKey {
  const code = e instanceof AuthError ? e.code : 'NETWORK';
  return (KNOWN.has(code) ? `err.${code}` : 'err.UNKNOWN') as MessageKey;
}

/**
 * 一封站内信。
 *
 * **文案不在这里。** 后端给的是 `tpl` + `params`，前端按 `tpl` 翻 ——
 * 和后端错误按 `code` 翻是同一条规矩：后端不知道这个玩家现在用哪种语言，
 * 也不该知道。存成一句话的话，他换语言之后信箱里会是一半一半，
 * 而那是历史记录，回头没法翻译。
 *
 * 运营手写的公告是例外：那种没有模板，只有 `title` / `body`。
 */
export interface Mail {
  id: number;
  kind: string;
  /** 模板键（`mail.withdrawPaid` 这种）。null = 运营手写的公告 */
  tpl: string | null;
  /** 模板参数。**金额是字符串**，用 Number() 读出来再按币种格式化 */
  params: Record<string, string | number | null>;
  title: string | null;
  body: string | null;
  read: boolean;
  ts: number | null;
  /** 附件（后台发的补偿/奖励）。null = 没有附件。**领取走 gateway**（GameApi.claimMail） */
  attachment?: MailAttachment | null;
}

export interface MailAttachment {
  coins: number;
  /** 领取时加到打码目标上的量。0 = 不带打码要求 */
  wager: number;
  claimed: boolean;
}

export interface MailPage {
  items: Mail[];
  unread: number;
  /** 下一页的游标。null = 没有下一页 */
  nextBefore: number | null;
  /** 没领的附件数 */
  unclaimed?: number;
}

export class AuthApi {
  #base: string;

  constructor(base: string) {
    this.#base = base.replace(/\/*$/, '/');
  }

  async #post<T>(path: string, body: unknown, token?: string): Promise<T> {
    let res: Response;
    try {
      res = await fetch(this.#base + path, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body ?? {}),
      });
    } catch (e) {
      throw new AuthError('NETWORK', String((e as Error)?.message ?? e), 0);
    }
    const text = await res.text();
    let parsed: Record<string, unknown> | null = null;
    try { parsed = text ? JSON.parse(text) : null; } catch { /* 见下 */ }
    if (!res.ok) {
      throw new AuthError(
        String(parsed?.code ?? `HTTP_${res.status}`),
        String(parsed?.msg ?? text.slice(0, 200)),
        res.status,
        parsed?.detail as Record<string, string> | undefined,
      );
    }
    if (parsed === null) throw new AuthError('BAD_RESPONSE', '服务器回了非 JSON', res.status);
    return parsed as T;
  }

  async #get<T>(path: string, token?: string): Promise<T> {
    let res: Response;
    try {
      // 没 token 就**一个头都不发**，别发 `Bearer undefined` ——
      // 那会让后端把它当成一次"凭据无效"，日志里多一条假的鉴权失败。
      res = await fetch(this.#base + path,
                        token ? { headers: { authorization: `Bearer ${token}` } } : {});
    } catch (e) {
      throw new AuthError('NETWORK', String((e as Error)?.message ?? e), 0);
    }
    const text = await res.text();
    let parsed: Record<string, unknown> | null = null;
    try { parsed = text ? JSON.parse(text) : null; } catch { /* 见下 */ }
    if (!res.ok) {
      throw new AuthError(String(parsed?.code ?? `HTTP_${res.status}`),
                          String(parsed?.msg ?? ''), res.status,
                          parsed?.detail as Record<string, string> | undefined);
    }
    if (parsed === null) throw new AuthError('BAD_RESPONSE', '', res.status);
    return parsed as unknown as T;
  }

  async phoneConfig() {
    const raw = await this.#get<unknown>('auth/phone-config');
    try { return decodePhoneConfig(raw); }
    catch { throw new AuthError('BAD_RESPONSE', 'Invalid phone configuration', 502); }
  }

  register(identifier: string, password: string, device?: string, ref?: string) {
    // ref 认不出来时后端**不报错**，当自然注册收下 ——
    // 一条过期或者打错的链接不该让人连注册都注册不了。
    return this.#post<Session>('auth/register',
                               { identifier, password, device, ref: ref || null });
  }

  login(identifier: string, password: string, device?: string) {
    return this.#post<Session>('auth/login', { identifier, password, device });
  }

  logout(token: string) {
    return this.#post<{ ok: boolean }>('auth/logout', {}, token);
  }

  whoami(token: string) {
    return this.#post<{ uid: string }>('auth/whoami', {}, token);
  }

  profile(token: string) {
    // 这一个是 GET —— 它是纯读，而且要能被浏览器缓存策略正常对待
    return this.#get<import('../profile/api.ts').AccountProfile>('profile', token);
  }

  setAvatar(token: string, avatar: string) {
    return this.#post<{ avatar: string }>('profile/avatar', { avatar }, token);
  }

  setNickname(token: string, nickname: string) {
    return this.#post<{ nickname: string; renamesLeft: number; renamesMax: number }>(
      'profile/nickname', { nickname }, token);
  }

  referralSummary(token: string) {
    return this.#get<import('../referral/api.ts').ReferralSummary>(
      'referral/summary', token);
  }

  downlines(token: string, level: number, offset = 0, n = 50) {
    return this.#get<import('../referral/api.ts').DownlinePage>(
      `referral/downlines?level=${level}&offset=${offset}&n=${n}`, token);
  }

  /** 自传头像。**发裸字节流，不用 multipart** —— 服务端少一个解析器就少一处攻击面。 */
  async uploadAvatar(token: string, file: Blob) {
    let res: Response;
    try {
      res = await fetch(this.#base + 'profile/avatar/upload', {
        method: 'POST',
        headers: { authorization: `Bearer ${token}` },
        body: file,
      });
    } catch (e) {
      throw new AuthError('NETWORK', String((e as Error)?.message ?? e), 0);
    }
    const text = await res.text();
    const parsed = text ? JSON.parse(text) as Record<string, unknown> : null;
    if (!res.ok) {
      throw new AuthError(String(parsed?.code ?? `HTTP_${res.status}`),
                          String(parsed?.msg ?? ''), res.status,
                          parsed?.detail as Record<string, string> | undefined);
    }
    return parsed as unknown as { avatar: string };
  }

  /** 收款身份。**号码是打码的** —— 后端不下发完整号码，前端也就无从泄露。 */
  payoutIdentity(token: string) {
    return this.#get<PayoutIdentity>('payout/identity', token);
  }

  /**
   * 绑定收款身份。**一个账号只能成功一次，而且不能自己解绑。**
   *
   * 号码原样发过去（带不带 `.` `-` 都行）—— 校验位在后端算。
   * 前端也做一遍的话，两份实现迟早会不一致，而"前端说对、后端说错"
   * 是最难解释给用户听的一种状态。
   */
  bindPayoutIdentity(token: string, cpf: string, holderName: string) {
    return this.#post<PayoutIdentity>('payout/identity', { cpf, holderName }, token);
  }

  /** 商品列表。 */
  products(token: string) {
    return this.#get<{ today: string; items: Product[] }>('shop/products', token);
  }

  /**
   * 下单。**只发 code** —— 价钱由服务端去库里查。
   * 让前端传价格的话，改一个数字就能一块钱买一万金币。
   */
  buy(token: string, code: string) {
    return this.#post<PurchaseOrder>('shop/orders', { code }, token);
  }

  /** 购买记录。 */
  purchases(token: string) {
    return this.#get<{ items: PurchaseOrder[] }>('shop/orders', token);
  }

  /** 单张订单。**收款界面靠它轮询"付了没有"。** */
  order(token: string, orderNo: string) {
    return this.#get<PurchaseOrder>(`shop/orders/${encodeURIComponent(orderNo)}`,
                                    token);
  }

  /** 一页站内信 + 未读数。**未读数和列表一起来** —— 分两次要的话，
   *  标完已读刷新列表时红点还是旧的。 */
  mail(token: string, before?: number | null, n = 20) {
    const q = before ? `&before=${before}` : '';
    return this.#get<MailPage>(`mail?n=${n}${q}`, token);
  }

  /** 标已读。**要么给 ids，要么 all** —— 后端也是这么要求的。 */
  readMail(token: string, body: { ids?: number[]; all?: boolean }) {
    return this.#post<{ marked: number; unread: number }>('mail/read', body, token);
  }

  /** 大厅的游戏列表。**不要 token** —— 没登录的人也该看得见有什么玩的。 */
  games() {
    return this.#get<{ games: GameCard[]; liveTables?: PpLiveFeed }>('games');
  }

  /**
   * 换一张进游戏用的启动令牌。**回的不是会话 token**：它 60 秒过期、
   * 一次性，而且只能在 gateway 那儿换成一张 play 会话（见 account/launch.py）。
   */
  launch(token: string, game: string, lang?: string) {
    return this.#post<LaunchTicket>('auth/launch', { game, lang }, token);
  }

  /** 一页投注历史。`before` 是上一页最后一条的 cursor。 */
  rounds(token: string, before?: string | null, n = 20) {
    const q = before ? `&before=${encodeURIComponent(before)}` : '';
    return this.#get<RoundPage>(`rounds?n=${n}${q}`, token);
  }

  sessions(token: string) {
    return this.#post<{ sessions: DeviceSession[] }>('auth/sessions', {}, token);
  }

  revokeOthers(token: string) {
    return this.#post<{ revoked: number }>('auth/sessions/revoke', { others: true }, token);
  }
}

/** 大厅卡片上的一个游戏。**这些字段全部来自后台**，前端不写死。 */
export interface GameCard {
  code: string;
  /** 默认名（英文）。**我们自己不翻** —— 它是商品名 */
  name: string;
  /** 厂商给的官方译名 `{ 'zh-CN': '麻将胡了' }`。没有的语言用 name，见 tabs/gameList.ts */
  names?: Record<string, string>;
  /** 副标题拆成结构化字段，**不是拼好的字符串** —— 拼好的没法翻，
   *  而且"线"的复数在英语葡语里还要变 */
  provider: string | null;
  /** 盘面形状，如 `3-4-3`。**来自玩法引擎**，运营改不动 */
  grid: string | null;
  lines: number | null;
  /** 发布状态；能否启动还需检查 available。 */
  status: string;
  /** 已上架但默认后端不可用时为 false；可选以兼容旧接口。 */
  available?: boolean;
  /** 累计成功付费游玩的去重人数；跨后端合并，服务端最多缓存 60 秒。 */
  playerCount?: number;
  /** 图标地址：`https://…` 或 `/路径`（相对游戏客户端的源）。
   *  **可能没有**，也可能有但 404 —— 两种都回到占位，见 tabs/gameIcon.ts */
  icon?: string | null;
}

/** PP DGA display feed. Table IDs are bound to platform game codes on the server. */
export interface PpLiveFeed {
  server: string;
  casinoId: string;
  currency: string;
  tables: { tableId: string; gameCode: string }[];
}

/**
 * 进游戏的票。**走哪个后端由服务端定**（后台可以把游戏切到 PG、开灰度），
 * 大厅不做任何判断，照着回包开：
 *
 *   inhouse   启动令牌，拼进我们自己的游戏客户端地址
 *   pg        account 上的一个相对地址，那一页再去 PG 拿游戏
 */
export type LaunchTicket =
  | { backend: 'inhouse'; token: string; expiresIn: number; game: string }
  | { backend: string; url: string; expiresIn: number; game: string };

/** 一局。**没有盘面数据** —— 要验一局走后台的重放，不是让客户端自己判。 */
export interface Round {
  status?: 'open' | 'aborted' | 'settled' | 'canceled';
  id: string;
  game: string;
  gameName: string;
  /** base / free。免费局的 bet 是 0，**但一样要显示** */
  mode: string;
  bet: string;
  win: string;
  adjustment?: string;
  at: string | null;
  cursor: string;
}

export interface RoundPage {
  rounds: Round[];
  more: boolean;
}

export interface DeviceSession {
  id: string;
  current: boolean;
  device: string | null;
  userAgent: string | null;
  ip: string | null;
  createdAt: string | null;
  idleSeconds: number;
}

/**
 * 这台设备叫什么。**只给人认**，不参与鉴权，所以怎么猜都不算错。
 *
 * 一律用中性/英文写法（`Chrome · iPhone`），**不翻译**：这个值会被存进
 * `session.device`，一存就固定了。用当时的界面语言写的话，用户换了语言之后
 * 设备列表里会混着几种语言 —— 而且后台看这张表的人也读不懂。
 */
export function deviceName(ua: string): string {
  const os = /iPhone|iPad/.test(ua) ? 'iPhone'
    : /Android/.test(ua) ? 'Android'
    : /Macintosh|Mac OS X/.test(ua) ? 'Mac'
    : /Windows/.test(ua) ? 'Windows'
    : 'Browser';
  const app = /CriOS|Chrome/.test(ua) ? 'Chrome'
    : /FxiOS|Firefox/.test(ua) ? 'Firefox'
    : /Edg\//.test(ua) ? 'Edge'
    : /Safari/.test(ua) ? 'Safari'
    : '';
  return app ? `${app} · ${os}` : os;
}
